/**
 * SqliteDatabase — Public API Types (§90 §1, §93 §2)
 *
 * Top-tier exported interface for the SecureBlob SQLite SDK.
 * One SqliteDatabase instance = one database file.
 * Supports multiple concurrent connections via WAL + SharedArrayBuffer.
 * Falls back to single-worker serialized mode when SAB is unavailable.
 *
 * Progressive activation:
 *   Phase 3: openDatabase → single connection, WAL mode
 *   Phase 4: Multiple connect() → separate workers
 *   Phase 5: startSync(), syncNow(), setSyncMode()
 *   Phase 6: Mode escalation, drain protocol
 */

import type { TransactionMode } from './pageChangeTracker'

// ── Database ──────────────────────────────────────────────────

export interface SqliteDatabase extends AsyncDisposable {
    /**
     * Create a new connection (may spawn a worker).
     * No mode declaration — WAL handles reader/writer at transaction level.
     */
    connect(options?: ConnectionOptions): Promise<SqliteConnection>

    /** Wait for local quiescence + durable local persistence by default. */
    flushLocal(options?: SqliteFlushLocalOptions): Promise<void>

    /** Wait for local quiescence plus remote publication / reconcile to settle. */
    flushRemote(): Promise<void>

    /**
     * Graceful shutdown.
     *
     * Default is offline-first: drain local work, but do not wait for remote
     * publication unless `flushRemote: true` is requested explicitly.
     */
    close(options?: SqliteCloseOptions): Promise<void>

    // Sync mode (§90 §3) — per database, changeable at runtime
    readonly syncMode: SyncMode
    /** Change sync mode. Waits for current cycle to complete if needed. */
    setSyncMode(mode: SyncMode): Promise<void>

    // Background sync
    /** Start background sync (may spawn sync worker). `interval` controls periodic remote-head polling. */
    startSync(options?: { interval?: number }): Promise<void>
    /** Stop sync. Waits for in-flight cycle to drain. */
    stopSync(): Promise<void>
    /** Trigger one-shot sync immediately without waiting for full remote idle. */
    syncNow(): Promise<SyncResult>
    /** Subscribe to sync events. Returns unsubscribe function. */
    onSync(callback: (event: SyncEvent) => void): () => void

    /**
     * Set a listener for sqlite3mt debug-stage events (§145).
     * When set, debug-stage events are forwarded to the listener instead of console.info.
     * Pass null to clear the listener.
     */
    setDebugStageListener(listener: ((payload: unknown) => void) | null): void

    readonly isOpen: boolean
    readonly connectionCount: number

    // ── Observability (§93 §3) ────────────────────────────────

    /**
     * Full diagnostic snapshot of database internals.
     * Zero-allocation on hot path — reads SAB atomics + cached state.
     *
     * Designed for:
     *   - SQL Studio UI panels (sync status, lock monitor, WAL graph)
     *   - Automated health checks
     *   - Advanced user scenarios (drain progress, conflict analysis)
     *
     * @example
     * ```ts
     * const diag = db.diagnostics()
     * console.log(diag.wal.frameCount, diag.sync.state)
     * ```
     */
    diagnostics(): DatabaseDiagnostics

    /**
     * Subscribe to diagnostic changes (debounced, max 10Hz).
     * Fires when any metric changes significantly.
     * Returns unsubscribe function.
     */
    onDiagnostics(callback: (diag: DatabaseDiagnostics) => void): () => void
}

// ── Connection ────────────────────────────────────────────────

export interface SqliteConnection extends AsyncDisposable {
    /**
     * Execute arbitrary SQL and return any rows produced by the statement.
     * Callers do not need to classify SQL as read-only vs mutating first.
     */
    execute<T = Record<string, unknown>>(sql: string, params?: SqlParam[], options?: QueryOptions): Promise<SqliteStatementResult<T>>

    /** Convenience wrapper for row-oriented reads. */
    query<T = Record<string, unknown>>(sql: string, params?: SqlParam[], options?: QueryOptions): Promise<T[]>

    /** Convenience wrapper for metadata-oriented writes. */
    exec(sql: string, params?: SqlParam[], options?: QueryOptions): Promise<ExecResult>

    /** Stream rows one by one for large result sets (TB-scale). */
    each<T = Record<string, unknown>>(sql: string, params?: SqlParam[], options?: QueryOptions): AsyncIterable<T>

    /** Prepared statement for hot-path performance. */
    prepare<T = Record<string, unknown>>(sql: string): Promise<PreparedStatement<T>>

    /**
     * Run fn inside a transaction.
     * BEGIN before, COMMIT after, ROLLBACK on throw.
     */
    transaction<T>(
        fn: (tx: SqliteTransaction) => Promise<T>,
        options?: TransactionOptions,
    ): Promise<T>

    close(): Promise<void>
    readonly isOpen: boolean
    /** Current database page count (nTruncate after last commit). */
    readonly pageCount: number

    /** Per-connection diagnostic snapshot. */
    diagnostics(): ConnectionDiag
}

// ── Transaction ───────────────────────────────────────────────

export interface SqliteTransaction {
    execute<T = Record<string, unknown>>(sql: string, params?: SqlParam[], options?: QueryOptions): Promise<SqliteStatementResult<T>>
    query<T = Record<string, unknown>>(sql: string, params?: SqlParam[], options?: QueryOptions): Promise<T[]>
    exec(sql: string, params?: SqlParam[], options?: QueryOptions): Promise<ExecResult>
    each<T = Record<string, unknown>>(sql: string, params?: SqlParam[], options?: QueryOptions): AsyncIterable<T>
}

// ── Prepared Statement ────────────────────────────────────────

export interface PreparedStatement<T> extends AsyncDisposable {
    /** Execute with params, return all rows. */
    all(params?: SqlParam[]): Promise<T[]>
    /** Execute with params, return first row or null. */
    get(params?: SqlParam[]): Promise<T | null>
    /** Execute with params, return ExecResult (for writes). */
    run(params?: SqlParam[]): Promise<ExecResult>
    /** Release resources. */
    finalize(): Promise<void>
}

// ── Supporting Types ──────────────────────────────────────────

export interface ExecResult {
    /** Number of rows changed by the statement. */
    changes: number
    /** RowId of the last INSERT, or 0 if no INSERT. */
    lastInsertRowId: bigint
}

export interface SqliteStatementResult<T = Record<string, unknown>> extends ExecResult {
    /** Rows produced by the executed SQL, if any. */
    rows: T[]
}

export interface QueryOptions {
    /** Cancel a long-running query. Worker calls sqlite3_interrupt(). */
    signal?: AbortSignal
}

export interface TransactionOptions {
    /**
     * true → BEGIN IMMEDIATE (grabs write lock upfront).
     * false/omitted → BEGIN DEFERRED (upgrades on first write).
     * Use IMMEDIATE in multi-worker WAL mode to avoid deadlock on lock upgrade.
     */
    immediate?: boolean
}

export interface ConnectionOptions {
    /** Debug label (shows in diagnostics/logging). */
    label?: string
}

export interface SqliteFlushLocalOptions {
    /**
     * true/omitted → wait until local sqlite3mt state is durable and quiescent.
     * false        → request the local checkpoint/sync driver and return after
     *                the request is scheduled. This is not a durability barrier.
     */
    wait?: boolean
}

export interface SqliteCloseOptions {
    /**
     * true  → perform a remote flush before shutdown
     * false → close after local drain only
     *
     * Default: false
     */
    flushRemote?: boolean
    /**
     * true/omitted → wait for local sqlite state to become durable on close
     * false        → skip the close-time local drain when the caller already
     *                flushed after each mutation and needs a fast shutdown
     */
    flushLocal?: boolean
}

export enum SyncMode {
    /** Mode 1: lease-based, one global writer at a time. */
    Sequential = 'sequential',
    /** Mode 2: optimistic CAS, concurrent multi-writer with merge. */
    Concurrent = 'concurrent',
}

export type SqlParam = string | number | boolean | null | Uint8Array | bigint

// ── Errors ────────────────────────────────────────────────────

export class SqliteError extends Error {
    constructor(
        message: string,
        /** SQLite result code (e.g. 5 for SQLITE_BUSY). */
        readonly code: number,
        /** Human-readable code name (e.g. 'SQLITE_BUSY'). */
        readonly codeName: string,
    ) {
        super(message)
        this.name = 'SqliteError'
    }
}

// ── Sync Events ───────────────────────────────────────────────

export type SyncEvent =
    | { type: 'sync-start' }
    | { type: 'sync-complete'; result: SyncResult }
    | { type: 'sync-error'; error: Error }
    | { type: 'remote-changes'; changedPages: number }
    | { type: 'mode-change'; from: SyncMode; to: SyncMode; reason: string }
    | { type: 'drain-requested'; initiator: string }
    | { type: 'drain-complete'; durationMs: number }
    | { type: 'lock-escalation'; from: SyncMode; to: SyncMode; trigger: LockEscalationTrigger }

export type LockEscalationTrigger =
    | 'schema-change'
    | 'interior-page'
    | 'predictive-split'
    | 'cas-retry-exhausted'
    | 'manual'

export interface SyncResult {
    direction: 'push' | 'pull' | 'merge' | 'no-op'
    pagesChanged: number
    conflicts: MergeConflict[]
    durationMs: number
}

export interface MergeConflict {
    table: string
    rowId: bigint
    column?: string
    localValue: unknown
    remoteValue: unknown
}

export type ConflictResolution = 'local' | 'remote' | 'skip'

// ══════════════════════════════════════════════════════════════════
// Observability Types (§93 §3)
// ══════════════════════════════════════════════════════════════════

/**
 * Full diagnostic snapshot — everything a SQL Studio needs to render
 * sync status panels, lock monitors, WAL graphs, and health dashboards.
 *
 * All fields are point-in-time reads (no async, no allocation on hot path).
 */
export interface DatabaseDiagnostics {
    /** Timestamp of this snapshot (monotonic). */
    readonly timestamp: number

    /** Database identifier. */
    readonly workspace: string

    // ── Memory ────────────────────────────────────────────────
    /** SAB memory usage. */
    readonly memory: MemoryDiag

    // ── WAL ───────────────────────────────────────────────────
    /** WAL file metrics. */
    readonly wal: WalDiag

    // ── Connections ───────────────────────────────────────────
    /** Per-connection diagnostics. */
    readonly connections: readonly ConnectionDiag[]

    // ── Sync ──────────────────────────────────────────────────
    /** Sync worker state. */
    readonly sync: SyncDiag

    // ── Locking ───────────────────────────────────────────────
    /** Lock state (from SAB₃ lock region). */
    readonly locks: LockDiag

    // ── Drain ─────────────────────────────────────────────────
    /** Mode 0 drain protocol state. */
    readonly drain: DrainDiag
}

/** SAB memory layout diagnostics. */
export interface MemoryDiag {
    /** SAB₁ size (database, bytes). */
    readonly sab1Size: number
    /** SAB₂ size (WAL, bytes). */
    readonly sab2Size: number
    /** SAB₃ size (SHM, bytes). */
    readonly sab3Size: number
    /** Total SAB allocation (bytes). */
    readonly totalSabBytes: number
    /** Page window: total slots. */
    readonly pageWindowSlots: number
    /** Page window: slots currently occupied. */
    readonly pageWindowUsed: number
    /** Page window: dirty (pinned) pages. */
    readonly dirtyPageCount: number
    /** Page size (bytes). */
    readonly pageSize: number
    /** Database size (pages). */
    readonly dbPageCount: number
    /** SharedArrayBuffer available? */
    readonly sabSupported: boolean
}

/** WAL file metrics (reads from SAB₂ + WalIndexHdr). */
export interface WalDiag {
    /** Current mxFrame (last valid frame index). */
    readonly mxFrame: number
    /** WAL data size (bytes). */
    readonly walDataSize: number
    /** Estimated frame count (walDataSize / frameSize). */
    readonly frameCount: number
    /** Last checkpoint frame. */
    readonly lastCheckpointFrame: number
    /** Frames since last checkpoint. */
    readonly framesSinceCheckpoint: number
    /** WAL auto-checkpoint threshold. */
    readonly autoCheckpointThreshold: number
}

/** Per-connection diagnostic info. */
export interface ConnectionDiag {
    /** Connection label (from ConnectionOptions). */
    readonly label: string
    /** Is the connection open? */
    readonly isOpen: boolean
    /** Currently in a transaction? */
    readonly inTransaction: boolean
    /** Transaction type (DEFERRED/IMMEDIATE) if active. */
    readonly transactionType?: 'deferred' | 'immediate'
    /** Number of active prepared statements. */
    readonly preparedStatementCount: number
    /** Last operation latency (ms). */
    readonly lastOperationMs: number
    /** Total queries executed on this connection. */
    readonly totalQueries: number
    /** Total writes executed on this connection. */
    readonly totalWrites: number
}

/** Sync worker diagnostics. */
export interface SyncDiag {
    /** Current sync mode (Mode 0/1/2). */
    readonly mode: SyncMode
    /** Sync worker lifecycle state. */
    readonly state: SyncState
    /** Is sync currently running? */
    readonly isSyncing: boolean
    /** Number of completed sync cycles. */
    readonly cycleCount: number
    /** Last sync result (null if no sync yet). */
    readonly lastResult: SyncResult | null
    /** Last sync error (null if no error). */
    readonly lastError: Error | null
    /** Time of last successful sync (Date.now()). */
    readonly lastSyncAt: number | null
    /** Time of next scheduled sync (Date.now()). */
    readonly nextSyncAt: number | null
    /** Dirty pages pending push. */
    readonly pendingPushPages: number
    /** Known remote version (CAS epoch). */
    readonly remoteEpoch: number
    /** Local version. */
    readonly localEpoch: number
    /** CAS retry count (current cycle). */
    readonly casRetryCount: number
    /** Total conflicts in last sync. */
    readonly lastConflictCount: number
    /** Lease ID (if Mode 1 active). */
    readonly leaseId: string | null
    /** Lease TTL remaining (ms, Mode 1 only). */
    readonly leaseTtlRemainingMs: number | null
}

export type SyncState =
    | 'idle'
    | 'starting'
    | 'syncing'
    | 'merging'
    | 'pushing'
    | 'pulling'
    | 'checkpointing'
    | 'stopped'
    | 'error'

/** Lock state (8 lock slots in SAB₃). */
export interface LockDiag {
    /** WRITE lock (slot 0): -1 = exclusive, 0 = free. */
    readonly writeLock: number
    /** CKPT lock (slot 1): -1 = exclusive, 0 = free. */
    readonly checkpointLock: number
    /** RECOVER lock (slot 2): -1 = exclusive, 0 = free. */
    readonly recoverLock: number
    /** READ locks (slots 3-7): count of shared holders. */
    readonly readLocks: readonly [number, number, number, number, number]
    /** Is the WAL write-locked? */
    readonly isWriteLocked: boolean
    /** ImportPending flag (sync worker holds priority). */
    readonly importPending: boolean
}

/** Mode 0 drain protocol diagnostics. */
export interface DrainDiag {
    /** Is Mode 0 exclusive access active? */
    readonly mode0Active: boolean
    /** Is a drain pending (responding to another client's Mode 0)? */
    readonly drainPending: boolean
    /** Are new transactions blocked? (mode0Active || drainPending). */
    readonly transactionsBlocked: boolean
    /** Drain initiator identity (if known). */
    readonly initiator: string | null
    /** Time drain started (Date.now()). */
    readonly drainStartedAt: number | null
    /** Time drain will timeout (Date.now()). */
    readonly drainTimeoutAt: number | null
}

// ── Factory ───────────────────────────────────────────────────

export interface DatabaseConfig {
    /** Workspace/database identifier. */
    workspace: string
    /** Session-owned sqlite3mt background sync policy for this database. */
    backgroundSync?: 'workspace-policy' | 'manual'
    /** Bootstrap ONLY: initial DB bytes (test-only full import). */
    initialDb?: Uint8Array
    /** Lazy loading: total page count from manifest. */
    pageCount?: number
    /** Lazy loading: page size from page 1 header. */
    pageSize?: number
    /** Lazy loading: seed pages (page 1 + first N) pre-loaded at open time. */
    seedPages?: Map<number, Uint8Array>
    /** Lazy loading: CAS page loader for on-demand fetch on cache miss. */
    onPageMiss?: (pgno: number) => Promise<Uint8Array | null>
    /** Max concurrent workers (default: navigator.hardwareConcurrency || 4). */
    maxConnections?: number
    /** Sync configuration (sync disabled if omitted). */
    sync?: SyncConfig
    /** Conflict resolution callback. */
    onConflict?: (conflict: MergeConflict) => ConflictResolution
    /**
     * Called after each SQLite transaction commit with dirty page data.
     * This is the persistence hook: the workspace uses it to encode
     * changed pages and commit them to storage incrementally.
     *
     * @param dirtyPages  Pages changed in this commit (read from VFS WAL-overlay)
     * @param mode        Transaction mode classification (0=schema, 1=structural, 2=data)
     * @param pageCount   Total DB page count after this commit (nTruncate)
     */
    onCommit?: (dirtyPages: Map<number, Uint8Array>, mode: TransactionMode, pageCount: number) => void | Promise<void>
}

export interface SyncConfig {
    /** Sync interval in ms (default: 5000). */
    interval?: number
    /** Auto-start sync on open (default: true). */
    autoStart?: boolean
    /** Initial sync mode (default: Concurrent). */
    preferredMode?: SyncMode
}

/**
 * Open a database. Loads WASM, allocates SABs, registers VFS.
 *
 * @example
 * ```ts
 * await using db = await openDatabase({ workspace: 'invoices' })
 * await using conn = await db.connect()
 * const rows = await conn.query<Invoice>('SELECT * FROM invoices')
 *
 * // Observability: render sync status in your UI
 * const diag = db.diagnostics()
 * statusBar.show(`WAL: ${diag.wal.frameCount} frames, Sync: ${diag.sync.state}`)
 * ```
 */
export { openDatabaseWithEngineFactory as openDatabase } from './realSqliteDatabase'
